import { ElementRef, Injectable } from '@angular/core';
import { SideBarMenuConfig } from '@horizon/core-navigations';
import { TranslateService } from '@ngx-translate/core';
import { SharedService } from '@core/services/shared.service';
import { SearchApiService } from '@core/services/search.service';
import { MenuRoutePaths } from '@shared/enums/routes.enums';
import { PermissionAccessType, PolicyService } from '@horizon/core';
import { take } from 'rxjs/operators';
import {
  SIDEBAR_ICON_HTML,
  SideBarIconKey,
} from './side-bar-menu-config.constants';

@Injectable({
  providedIn: 'root',
})
export class SideBarMenuConfigService {
  public sidebarMenuConfig: SideBarMenuConfig = new SideBarMenuConfig();
  private readonly defaultLevelOneIcon =
    SIDEBAR_ICON_HTML[SideBarIconKey.DefaultLevelOne];
  private readonly settingsIcon =
    SIDEBAR_ICON_HTML[SideBarIconKey.Settings];

  constructor(
    private translate: TranslateService,
    private sharedService: SharedService,
    public searchApiService: SearchApiService,
    private policyService: PolicyService
  ) {
    //Default values
    this.sidebarMenuConfig.menuSettings = {
      ...this.sidebarMenuConfig.menuSettings,
      burguerIcon: SIDEBAR_ICON_HTML[SideBarIconKey.Toggle],
      closeIcon: SIDEBAR_ICON_HTML[SideBarIconKey.Toggle],
      menuBackgroundColor: '#ffffff',
      menuItemSelectedBackgroundColor: '#ffffff',
    };

    this.rebuildMenuItems();

    this.sharedService.menuItems$.subscribe(() => this.rebuildMenuItems());

    this.sidebarMenuConfig.horizonModules = [
      {
        icon: `<svg width="50" height="50" viewBox="0 0 50 50" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M0 0H50V40C50 45.5228 45.5228 50 40 50H0V0Z" fill="#002841"/>
        <path d="M34.0026 20.4042V20.4273C34.0026 20.4042 34.0026 20.3811 34.0026 20.3811C34.0026 16.4296 31.7842 10.2597 25.0597 10.0055C25.0597 10.0055 20.8771 9.77439 18.3352 12.3163C16.9255 13.6797 16.0936 15.7594 16.0012 17.0997V17.4232C16.0012 19.0408 18.2889 19.7109 19.7679 18.7404C21.7783 17.4001 21.5703 15.6208 24.5282 15.4128C27.5553 15.2279 28.9187 17.8623 28.9187 20.3811C28.9187 22.8999 27.5553 25.5342 24.5282 25.3494C21.5934 25.1645 21.8014 23.362 19.7679 22.0218C18.312 21.0512 16.0936 21.7445 16.0012 23.2234V35.4939C16.0012 35.4939 15.6546 40 20.6691 40C21.0157 40 21.1312 39.6996 21.1312 39.3761V29.6013C21.1312 29.6013 22.5871 30.3176 25.1983 30.2252C32.177 30.0172 34.0026 24.5175 34.0026 20.4042Z" fill="white"/>
        </svg>
        `,
        name: 'pepper',
        url: '#',
      },
    ];
  }

  public setMenuColors(host: ElementRef<HTMLElement>): void {
    //REQUIRED FOR STYLINGS OF THE SYNCFUSION MENU --> Call it from the parent component
    host.nativeElement.style.setProperty(
      `--menu-background-color`,
      this.sidebarMenuConfig.menuSettings.menuBackgroundColor
    );
    host.nativeElement.style.setProperty(
      `--menu-item-selected-background-color`,
      this.sidebarMenuConfig.menuSettings.menuItemSelectedBackgroundColor
    );
  }

  refreshMenuItemsWhenPermissionsReady(): void {
    const permissions$ = this.policyService.getCurrentUserPermissions();
    if (!permissions$) {
      return;
    }

    permissions$.pipe(take(1)).subscribe({
      next: (permissions) => {
        if (permissions) {
          localStorage.setItem(
            'currentUserPermissions',
            JSON.stringify(permissions)
          );
        }
        this.rebuildMenuItems();
      },
      error: () => this.rebuildMenuItems(),
    });
  }

  private rebuildMenuItems(): void {
    this.sidebarMenuConfig.menuItems = this.applyLevelOneIcons([
      ...this.sharedService.menuItems.getValue(),
      ...this.getStaticMenuItems(),
    ]);
  }

  getStaticMenuItems() {
    const permissions = this.getStoredUserPermissions();
    const hasConfigPermission = permissions.some(
      (permission) =>
        permission.permissionName === 'Config' &&
        permission.permissionAccessType === PermissionAccessType.READ
    );
    if (hasConfigPermission) {
      return [
        {
          nodeId: '100',
          nodeText: 'menu.settings',
          icon: this.settingsIcon,
          nodeChild: [
            {
              nodeId: '100-01',
              nodeText: 'menu.users',
              icon: SIDEBAR_ICON_HTML[SideBarIconKey.Users],
              url: MenuRoutePaths.SUPER_USER_USERS,
            },
            {
              nodeId: '100-02',
              nodeText: 'menu.roles',
              icon: SIDEBAR_ICON_HTML[SideBarIconKey.Roles],
              url: MenuRoutePaths.SUPER_USER_ROLES_AND_PERMISSIONS,
            },
            {
              nodeId: '100-03',
              nodeText: 'menu.scopes',
              icon: SIDEBAR_ICON_HTML[SideBarIconKey.Scopes],
              url: MenuRoutePaths.SUPER_USER_SCOPES_GROUPS,
            },
            {
              nodeId: '100-04',
              nodeText: 'menu.tableau_configs',
              icon: SIDEBAR_ICON_HTML[SideBarIconKey.TableauConfigs],
              url: MenuRoutePaths.SUPER_USER_CONFIGS,
            },
            {
              nodeId: '100-05',
              nodeText: 'menu.hubspot_configs',
              icon: SIDEBAR_ICON_HTML[SideBarIconKey.HubspotConfigs],
              url: MenuRoutePaths.SUPER_USER_HUBSPOT_CONFIGS,
            },
            {
              nodeId: '100-06',
              nodeText: 'menu.clients',
              icon: SIDEBAR_ICON_HTML[SideBarIconKey.Clients],
              url: MenuRoutePaths.SUPER_USER_CLIENTS,
            },
          ],
        },
      ];
    } else {
      return [];
    }
  }

  private getStoredUserPermissions(): any[] {
    const raw = localStorage.getItem('currentUserPermissions');
    if (!raw) {
      return [];
    }

    try {
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }

  private applyLevelOneIcons(menuItems: any[]): any[] {
    return menuItems.map((menuItem) => {
      const nodeText = String(menuItem?.nodeText ?? '').toLowerCase();
      const nodeId = String(menuItem?.nodeId ?? '');
      const isLevelOne = !nodeId.includes('-');
      const isSettings = nodeText === 'menu.settings';

      if (isLevelOne && !isSettings) {
        return {
          ...menuItem,
          icon: this.defaultLevelOneIcon,
        };
      }

      if (isLevelOne && isSettings) {
        return {
          ...menuItem,
          icon: this.settingsIcon,
        };
      }

      return menuItem;
    });
  }
}
